import React, { useEffect, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook, Linkedin } from 'lucide-react';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { useSettings } from '../lib/SettingsContext';
import { useLang, useLocalizedHref } from '../lib/i18n';

const NAV_KEYS = [
  { key: 'nav.quienes', path: '/quienes-somos' },
  { key: 'nav.ofrecemos', path: '/que-ofrecemos' },
  { key: 'nav.experiencias', path: '/experiencias' },
  { key: 'nav.noticias', path: '/noticias' },
  { key: 'nav.colaboradores', path: '/colaboradores' },
  { key: 'nav.contacto', path: '/contacto' },
];

export default function Header() {
  const { settings } = useSettings();
  const { ui } = useLang();
  const href = useLocalizedHref();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const NAV_LINKS = NAV_KEYS.map((n) => ({ label: ui(n.key), path: href(n.path) }));

  // Split menu: left side first 2-3, right side rest, with the small Logo in the middle
  const leftLinks = NAV_LINKS.slice(0, 3);
  const rightLinks = NAV_LINKS.slice(3);

  return (
    <>
      {/* Top utility bar */}
      <div
        className="w-full flex justify-between items-center px-5 md:px-10 py-2 text-xs font-sans-ui tracking-widest text-white/80"
        style={{ background: 'rgba(0,0,0,0.55)' }}
      >
        <div className="flex items-center gap-3">
          <span className="uppercase text-[10px] tracking-[0.3em] text-white/70">{ui('nav.follow_us')}:</span>
          {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noreferrer" className="hover:text-[#d4663a] transition-colors"><Instagram size={15} /></a>}
          {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noreferrer" className="hover:text-[#d4663a] transition-colors"><Facebook size={15} /></a>}
          {settings.social_linkedin && <a href={settings.social_linkedin} target="_blank" rel="noreferrer" className="hover:text-[#d4663a] transition-colors"><Linkedin size={15} /></a>}
        </div>
        <div className="hidden md:flex items-center gap-5 text-[10px] tracking-[0.3em] uppercase text-white/70">
          <Link to={href('/contacto')} className="hover:text-[#d4663a] transition-colors">{ui('nav.newsletter')}</Link>
          <LanguageSwitcher />
        </div>
      </div>

      <header
        className="sticky top-0 z-40 transition-all duration-500"
        style={{
          background: scrolled ? 'rgba(13,13,14,0.92)' : 'rgba(13,13,14,0.45)',
          backdropFilter: 'blur(14px)',
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between gap-6">
          {/* Left nav */}
          <nav className="hidden lg:flex items-center gap-6 flex-1 justify-end">
            {leftLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                className={({ isActive }) =>
                  `gd-link font-serif-display text-[14px] tracking-wide transition-colors ${isActive ? 'text-[#e08054]' : 'text-[#d4663a] hover:text-[#e8946a]'}`
                }
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Small logo center */}
          <div className="flex-shrink-0">
            <Logo size="sm" />
          </div>

          {/* Right nav */}
          <nav className="hidden lg:flex items-center gap-6 flex-1">
            {rightLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                className={({ isActive }) =>
                  `gd-link font-serif-display text-[14px] tracking-wide transition-colors ${isActive ? 'text-[#e08054]' : 'text-[#d4663a] hover:text-[#e8946a]'}`
                }
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="lg:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <button className="text-white p-2" onClick={() => setOpen((v) => !v)} aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#0d0d0e]">
            <nav className="flex flex-col px-6 py-4">
              <NavLink to={href('/')} end className={({ isActive }) => `py-3 font-serif-display text-[17px] border-b border-white/5 ${isActive ? 'text-[#e08054]' : 'text-[#d4663a]'}`}>{ui('nav.home')}</NavLink>
              {NAV_LINKS.map((l) => (
                <NavLink key={l.path} to={l.path} className={({ isActive }) => `py-3 font-serif-display text-[17px] border-b border-white/5 ${isActive ? 'text-[#e08054]' : 'text-[#d4663a]'}`}>{l.label}</NavLink>
              ))}
              <div className="flex items-center gap-5 pt-5">
                {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noreferrer" className="text-white/70"><Instagram size={18} /></a>}
                {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noreferrer" className="text-white/70"><Facebook size={18} /></a>}
                {settings.social_linkedin && <a href={settings.social_linkedin} target="_blank" rel="noreferrer" className="text-white/70"><Linkedin size={18} /></a>}
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
